/*
 * @Author: Ryan
 * @Date: 2025-12-26
 * @Description: Frequently Asked Questions section with expandable items
 */
import React, { useState } from 'react';

interface FAQItem {
  id: string;
  question: string;
  answer: string | React.ReactNode;
}

interface FAQItemProps {
  item: FAQItem;
  isExpanded: boolean;
  onClick: () => void;
}

const FAQItemComponent: React.FC<FAQItemProps> = ({ item, isExpanded, onClick }) => {
  return (
    <div
      className={`
        border border-[#CDCDCD] transition-all duration-300 cursor-pointer
        ${isExpanded ? 'bg-white' : 'bg-white hover:bg-gray-50'}
      `}
      onClick={onClick}
    >
      <div className="px-6 py-6 pad:px-8 pad:py-8">
        {/* 问题行 */}
        <div className="flex items-center justify-between gap-4">
          {/* 问题文本 */}
          <h3 className="flex-1 text-base pad:text-base font-semibold text-[#0F1117]">
            {item.question}
          </h3>

          {/* +/- 图标 */}
          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
            {isExpanded ? (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 12H4"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            )}
          </div>
        </div>

        {/* 答案 - 展开时显示 */}
        <div
          className={`
            overflow-hidden transition-all duration-300
            ${isExpanded ? 'max-h-[500px] opacity-100 mt-4' : 'max-h-0 opacity-0'}
          `}
        >
          <div className="text-sm pad:text-base text-gray-600 leading-relaxed">
            {item.answer}
          </div>
        </div>
      </div>
    </div>
  );
};

const FAQSection: React.FC = () => {
  // 使用 Set 来跟踪展开的问题 ID，允许同时展开多个
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(['faq-1']));

  const faqItems: FAQItem[] = [
    {
      id: 'faq-1',
      question: 'What is an AI avatar?',
      answer:
        'An AI avatar is a digital human created with artificial intelligence. These avatars can talk, show facial expressions, and use gestures like real people, making them useful for video creation, training, marketing, and virtual communication. FalcoCut AI Avatars are built to look natural and professional while being quick and scalable for teams and creators.',
    },
    {
      id: 'faq-2',
      question: 'What is an AI avatar generator?',
      answer:
        'An AI avatar generator is a tool that transforms text, images, or short recordings into lifelike digital presenters. With FalcoCut, you can turn scripts into talking videos in minutes—no cameras, actors, or editing software needed.',
    },
    {
      id: 'faq-3',
      question: 'Is there a free AI avatar generator?',
      answer:
        'Yes. FalcoCut offers a free plan that lets you try AI avatars, generate short talking videos, and add subtitles or voices. For advanced customization, HD/4K export, and commercial rights, upgrade to a paid plan.',
    },
    {
      id: 'faq-4',
      question: 'Can I create a custom avatar of myself or a teammate?',
      answer:
        'Absolutely. You can upload a photo or record a short video to generate a personal digital twin. This avatar mirrors your appearance, expressions, and even your voice, so you can create professional content without always being on camera.',
    },
    {
      id: 'faq-5',
      question: 'Which is the best AI avatar generator in 2026?',
      answer:
        'FalcoCut stands out as one of the best AI avatar generators thanks to its balance of speed, realism, and flexibility. You can create avatars for e-learning, e-commerce, or social media in 30+ languages. Other popular platforms include HeyGen, VEED.IO, and Rask.ai, but FalcoCut offers an all-in-one solution designed for global-ready content.',
    },
    {
      id: 'faq-6',
      question: 'How do I create an AI avatar video with FalcoCut?',
      answer: (
        <>
          <p className="mb-3">It&apos;s simple:</p>
          <ol className="list-decimal pl-5 space-y-2">
            <li>Sign up for free.</li>
            <li>Pick a stock avatar or create your own.</li>
            <li>Enter your script.</li>
            <li>Select a voice or clone your own.</li>
            <li>Generate your avatar video and export in minutes.</li>
          </ol>
        </>
      ),
    },
    {
      id: 'faq-7',
      question: 'Do FalcoCut AI avatars support multiple languages?',
      answer:
        'Yes. FalcoCut avatars can speak in over 30 languages and dialects. With multilingual voice support and cloning, you can localize your content for any market.',
    },
    {
      id: 'faq-8',
      question: "How realistic are FalcoCut's AI avatars?",
      answer:
        "They're designed to look and sound human. Our avatars capture natural expressions, lip sync, and gestures so that they feel authentic and engaging for your audience.",
    },
    {
      id: 'faq-9',
      question: 'Can AI avatars show emotions and gestures?',
      answer:
        "Yes. FalcoCut avatars are expressive. They smile, pause, and use body language that matches the script's tone, making videos feel more alive than static subtitles or robotic voices.",
    },
    {
      id: 'faq-10',
      question: 'Does FalcoCut offer pre-made or industry-specific avatars?',
      answer:
        "Yes. You'll find avatars designed for business, marketing, education, healthcare, and more. These ready-to-use avatars make it easy to start creating without setup.",
    },
    {
      id: 'faq-11',
      question: 'How does FalcoCut save time compared to traditional video production?',
      answer:
        'Traditional videos require cameras, filming, and editing. With FalcoCut, you just type your script, select an avatar, and generate. This reduces costs and makes video creation scalable for ads, training, and campaigns.',
    },
  ];

  const handleItemClick = (id: string) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <section className="w-full bg-white py-12 pad:py-16 sm:py-20 px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* 标题 */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-[#0F1117] text-center mb-10 pad:mb-12 sm:mb-16">
          Frequently Asked Questions
        </h2>

        {/* FAQ 列表 - 840px 宽度居中 */}
        <div className="max-w-[840px] mx-auto flex flex-col gap-2">
          {faqItems.map((item) => (
            <FAQItemComponent
              key={item.id}
              item={item}
              isExpanded={expandedIds.has(item.id)}
              onClick={() => handleItemClick(item.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
